/**
 * Environment Detection Module
 * 
 * This module provides a centralized way to detect and manage the current
 * environment (development, production, test) and related configuration.
 */

export type Environment = 'development' | 'production' | 'test';

/**
 * Get the current environment
 * Uses NODE_ENV by default, falls back to 'development' if not set
 */
export const getEnvironment = (): Environment => {
  const env = process.env.NODE_ENV || 'development';
  
  // Validate the environment
  if (['development', 'production', 'test'].includes(env)) {
    return env as Environment;
  }
  
  console.warn(`[ENV] Unknown NODE_ENV "${env}", defaulting to "development"`);
  return 'development';
};

/**
 * Check if the current environment matches the given environment
 */
export const isEnvironment = (env: Environment): boolean => {
  return getEnvironment() === env;
};

/**
 * Check if the code is running in a browser environment
 */
// Environment detection functions
export const isBrowser = (): boolean => typeof window !== 'undefined';

export const isServer = (): boolean => !isBrowser();

export const isTest = (): boolean => isEnvironment('test');

export const isProduction = (): boolean => isEnvironment('production');

export const isDevelopment = (): boolean => isEnvironment('development');

/**
 * Get a debug flag value
 * In production, all debug flags are forced to false
 */
export const getDebugFlag = (flagName: string, defaultValue = false): boolean => {
  if (isProduction()) {
    return false; // Never enable debug flags in production
  }
  
  // Check process.env for server-side
  if (isServer() && process.env[flagName] !== undefined) {
    return process.env[flagName] === 'true';
  }
  
  // Check window for client-side
  if (isBrowser() && (window as any)[flagName] !== undefined) {
    return !!(window as any)[flagName];
  }
  
  return defaultValue;
};

/**
 * Check if E2E testing is enabled
 */
export const isE2E = (): boolean => {
  // В браузере проверяем window.PLAYWRIGHT_E2E
  if (isBrowser() && (window as any).PLAYWRIGHT_E2E) {
    return true;
  }
  
  // В тестовом окружении
  if (isTest()) {
    return true;
  }
  
  // Через process.env для серверной части
  if (isServer() && process.env.PLAYWRIGHT_E2E === 'true') {
    return true;
  }
  
  return false;
};

/**
 * Check if debug panel should be shown
 */
export const showDebugPanel = (): boolean => {
  const dev = isDevelopment();
  return getDebugFlag('SHOW_PSYCO_DEBUG', dev);
};

/**
 * Get the appropriate collection name based on environment
 * Always returns 'results' for production, 'results_test' for local development and testing
 */
export const getCollectionName = (): string => {
  // В браузере проверяем, локальный ли это билд
  if (isBrowser()) {
    const isLocalBuild = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname.includes('172.20.10.4');
    
    if (isLocalBuild) {
      console.log('[ENV] Локальный билд - используем тестовую коллекцию (results_test)');
      return 'results_test';
    }
  }
  
  // В production всегда используем 'results' коллекцию
  if (isProduction()) {
    console.log('[ENV] Production - используем production коллекцию (results)');
    return 'results';
  }
  
  // В тестовом окружении используем 'results_test'
  if (isTest()) {
    console.log('[ENV] Тестовое окружение - используем тестовую коллекцию (results_test)');
    return 'results_test';
  }
  
  // По умолчанию для development используем 'results_test'
  console.log('[ENV] Development - используем тестовую коллекцию (results_test)');
  return 'results_test';
};

// Log the current environment configuration
if (isBrowser) {
  console.log('[ENV] Current environment:', {
    environment: getEnvironment(),
    isProduction,
    isDevelopment,
    isTest,
    isE2E,
    showDebugPanel,
    collection: getCollectionName(),
    userAgent: navigator.userAgent,
    location: window.location.href,
  });
}
